import React, { useEffect, useState, useRef } from "react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ReactPixel from "react-facebook-pixel";
import TagManager from "react-gtm-module";
import { trackEvent } from "../../services/events";

const WHATSAPP_NUMBER = "+97165674835";

const DraggableWhatsAppIcon = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isDragged, setIsDragged] = useState(false);
  const offset = useRef({ x: 0, y: 0 });
  const iconRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0 });

  // ── Drag handlers ────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    startPos.current = { x: clientX, y: clientY };

    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      offset.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
      if (!isDragged) {
        setPosition({ x: rect.left, y: rect.top });
        setIsDragged(true);
      }
    }
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as MouseEvent).clientX;
      clientY = (e as MouseEvent).clientY;
    }
    setPosition({
      x: clientX - offset.current.x,
      y: clientY - offset.current.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove, { passive: false });
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleMouseMove, { passive: false });
      window.addEventListener("touchend", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging]);

  // ── Smart WhatsApp click ─────────────────────────────────────────────────
  const handleClick = async (e: React.MouseEvent) => {
    // Ignore if the user was dragging
    const distance = Math.sqrt(
      Math.pow(e.clientX - startPos.current.x, 2) +
      Math.pow(e.clientY - startPos.current.y, 2)
    );
    if (distance > 5) {
      e.preventDefault();
      return;
    }

    // Track custom event
    try {
      await trackEvent({
        event_name: {
          web: "Kirkos_WhatsApp_FloatingIcon_Click",
          app: "Kirkos_WhatsApp_FloatingIcon_Click_App",
        },
        event_category: "Contact_Us",
        module: "General",
        payload: {},
      });
    } catch (_) { }

    // Meta Pixel tracking
    if (process.env.REACT_APP_META_PIXEL_ID) {
      try {
        ReactPixel.track("WhatsApp_Click", {
          content_name: "WhatsApp Floating Button",
          content_category: "Floating CTA",
          content_type: "Button Click",
          button_location: "Draggable Icon",
          value: 0,
          currency: "AED",
        });
      } catch (error) {
        console.error("Meta Pixel tracking failed:", error);
      }
    }

    // GTM tracking
    try {
      TagManager.dataLayer({
        dataLayer: {
          event: "whatsapp_click",
          page_path: window.location.pathname,
          page_url: window.location.href,
        }
      });
    } catch (_) { }

    const cleanNumber = WHATSAPP_NUMBER.replace(/\+/g, "");
    const defaultMessage = encodeURIComponent("Hello Kirkos! I'm interested in renting a car and have a few questions. Could you please assist me?");
    const mobileWhatsApp = `https://wa.me/${cleanNumber}?text=${defaultMessage}`;
    const webWhatsApp = `https://web.whatsapp.com/send?phone=${cleanNumber}&text=${defaultMessage}`;
    const desktopWhatsApp = `whatsapp://send?phone=${cleanNumber}&text=${defaultMessage}`;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const focusExistingWhatsAppTab = () => {
      const whatsappTab = window.open("", "whatsapp");
      if (
        whatsappTab &&
        !whatsappTab.closed &&
        whatsappTab.location.href.includes("web.whatsapp.com")
      ) {
        whatsappTab.focus();
        return true;
      }
      return false;
    };

    if (isMobile) {
      // Universal links (wa.me) are the most reliable way on iOS/Android
      // They automatically trigger the "Open in WhatsApp?" prompt or open the app directly.
      window.location.href = mobileWhatsApp;
    } else {
      // Desktop: try native app, open web WhatsApp in named tab as fallback
      let iframe: HTMLIFrameElement | null = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      const handleBlur = () => {
        if (iframe && document.body.contains(iframe)) {
          clearTimeout(desktopAppTimeout);
          document.body.removeChild(iframe);
          iframe = null;
        }
        window.removeEventListener("blur", handleBlur);
        window.removeEventListener("focus", handleFocus);
      };

      const handleFocus = () => {
        setTimeout(() => {
          if (iframe && document.body.contains(iframe)) {
            clearTimeout(desktopAppTimeout);
            document.body.removeChild(iframe);
            iframe = null;
            if (!focusExistingWhatsAppTab()) {
              window.open(webWhatsApp, "whatsapp");
            }
          }
        }, 500);
      };

      const desktopAppTimeout = setTimeout(() => {
        if (iframe && document.body.contains(iframe)) {
          document.body.removeChild(iframe);
          iframe = null;
          if (!focusExistingWhatsAppTab()) {
            window.open(webWhatsApp, "whatsapp");
          }
        }
      }, 1500);

      window.addEventListener("blur", handleBlur);
      window.addEventListener("focus", handleFocus);
      iframe.src = desktopWhatsApp;
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>
        {`
          @keyframes glow-pulse {
            0%   { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); }
            70%  { box-shadow: 0 0 0 20px rgba(37, 211, 102, 0); }
            100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
          }
          .whatsapp-drag-icon {
            transition: transform 0.3s ease;
          }
          .whatsapp-drag-icon:hover {
            transform: scale(1.1);
          }
        `}
      </style>
      <div
        ref={iconRef}
        className="whatsapp-drag-icon"
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onClick={handleClick}
        style={{
          position: "fixed",
          left: isDragged ? `${position.x}px` : undefined,
          top: isDragged ? `${position.y}px` : undefined,
          bottom: isDragged ? undefined : "30px",
          right: isDragged ? undefined : "30px",
          width: "65px",
          height: "65px",
          backgroundColor: "#25D366",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          boxShadow: "0px 6px 15px rgba(0,0,0,0.3)",
          cursor: isDragging ? "grabbing" : "pointer",
          zIndex: 9999,
          userSelect: "none",
          touchAction: "none",
          animation: isDragging ? "none" : "glow-pulse 2s infinite",
        }}
        title="Chat with us on WhatsApp"
      >
        <WhatsAppIcon style={{ fontSize: "40px" }} />
      </div>
    </>
  );
};

export default DraggableWhatsAppIcon;
